/* PreviewLibrary.jsx — Browse page for the 143 AI-Agent Updated Specialization Previews.
   100% dynamic: tabs, groups, cards, counts, filters all derive from the JSON.
   UI: playful & colorful "After 10th/Inter" style — warm cream bg, pastel accents. */
import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Search, SlidersHorizontal, TrendingUp, Bot, CheckCircle2,
  AlertTriangle, Loader2, ChevronRight, GraduationCap, Briefcase, LineChart, X,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { loadPreviewLibrary, validateLibrary, programSlug, PROGRAM_ORDER } from '../lib/previewLibrary';
import { isLocked } from '../lib/contentLoader';
import ContentLockedCard from '../components/ContentLockedCard';
import StageFilterBar, { useStageFilter } from '../components/StageFilterBar';
import { useAuth } from '../lib/auth';

const PROGRAM_ICONS = { 'B.Tech': GraduationCap, BBA: Briefcase, MBA: LineChart };
const PROGRAM_GRADS = {
  'B.Tech': 'from-teal-400 to-emerald-500',
  BBA: 'from-amber-400 to-orange-500',
  MBA: 'from-indigo-400 to-violet-500',
};
/* rotating pastel accents for branch groups */
const GROUP_ACCENTS = [
  { border: 'border-teal-200', dot: 'bg-teal-500', hover: 'hover:border-teal-400' },
  { border: 'border-amber-200', dot: 'bg-amber-500', hover: 'hover:border-amber-400' },
  { border: 'border-rose-200', dot: 'bg-rose-500', hover: 'hover:border-rose-400' },
  { border: 'border-indigo-200', dot: 'bg-indigo-500', hover: 'hover:border-indigo-400' },
  { border: 'border-emerald-200', dot: 'bg-emerald-500', hover: 'hover:border-emerald-400' },
  { border: 'border-purple-200', dot: 'bg-purple-500', hover: 'hover:border-purple-400' },
];

/* score filter buckets — applied to snapshot values 1..5 */
const DEMAND_OPTIONS = [
  { value: 'any', label: 'Any demand' },
  { value: '5', label: 'Very high (5)' },
  { value: '4', label: 'High (4+)' },
  { value: '3', label: 'Medium (3+)' },
];
const IMPACT_OPTIONS = [
  { value: 'any', label: 'Any AI impact' },
  { value: '5', label: 'Very high (5)' },
  { value: '4', label: 'High (4+)' },
  { value: '3', label: 'Medium (3+)' },
];
const RISK_OPTIONS = [
  { value: 'any', label: 'Any automation risk' },
  { value: 'low', label: 'Low (1–2)' },
  { value: 'medium', label: 'Medium (3)' },
  { value: 'high', label: 'High (4–5)' },
];

const ScoreBadge = ({ icon: Icon, label, value, tone }) => {
  const tones = {
    teal: 'bg-teal-50 text-teal-800 border-teal-200',
    blue: 'bg-indigo-50 text-indigo-800 border-indigo-200',
  };
  return (
    <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full border ${tones[tone]}`}>
      <Icon size={12} /> {label} {value}/5
    </span>
  );
};

const PreviewCard = ({ rec, accent, showProgram = false }) => (
  <Link
    to={`/previews/${programSlug(rec.program)}/${rec.id}`}
    className={`group bg-white border-2 ${accent.border} ${accent.hover} rounded-3xl p-5 shadow-sm hover:shadow-md transition-all duration-200 flex flex-col gap-3`}
    data-testid={`preview-card-${programSlug(rec.program)}-${rec.id}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div>
        {showProgram && (
          <span className={`inline-block text-[11px] font-extrabold text-white bg-gradient-to-r ${PROGRAM_GRADS[rec.program]} px-2.5 py-0.5 rounded-full mb-1.5`}>{rec.program}</span>
        )}
        <h3 className="font-extrabold text-gray-900 text-base leading-snug group-hover:text-teal-800 transition-colors">{rec.title}</h3>
        <p className="text-sm text-gray-500 mt-1">{rec.branch_or_stream}</p>
      </div>
      <span className={`w-8 h-8 rounded-full ${accent.dot} text-white flex items-center justify-center shrink-0 opacity-80 group-hover:opacity-100 transition-opacity`}>
        <ChevronRight size={15} />
      </span>
    </div>
    <div className="flex flex-wrap gap-2 mt-auto">
      <ScoreBadge icon={TrendingUp} label="Future Demand" value={rec.snapshot?.future_demand} tone="teal" />
      <ScoreBadge icon={Bot} label="AI-Agent Impact" value={rec.snapshot?.ai_agent_impact} tone="blue" />
    </div>
  </Link>
);

export default function PreviewLibrary() {
  const { user } = useAuth();
  const { showAll, setShowAll } = useStageFilter();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(false);
  const [activeProgram, setActiveProgram] = useState('B.Tech');
  const [search, setSearch] = useState('');
  const [branch, setBranch] = useState('any');
  const [demand, setDemand] = useState('any');
  const [impact, setImpact] = useState('any');
  const [risk, setRisk] = useState('any');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    loadPreviewLibrary()
      .then((d) => { if (isLocked(d)) { setLocked(true); return; } setData(d); })
      .catch((e) => setError(e.message));
  }, []);

  const validation = useMemo(() => (data ? validateLibrary(data) : null), [data]);

  const programs = data?.programs || {};
  // Stage-based default program: after-inter → B.Tech (broadly); college → B.Tech;
  // working/final-year → MBA. We don't restrict the program list, just default the
  // active tab so users see relevant content first when they land.
  const stageDefault = (() => {
    const s = user?.lexi_stage;
    if (!s || showAll) return null;
    if (s === 'after-inter' || s === 'college') return 'B.Tech';
    if (s === 'working' || s === 'final-year') return 'MBA';
    return null;
  })();
  const programNames = PROGRAM_ORDER.filter((p) => programs[p]);

  useEffect(() => {
    if (stageDefault && programs[stageDefault] && activeProgram !== stageDefault) {
      setActiveProgram(stageDefault);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stageDefault, programs]);

  /* branch options for the active program, in JSON order */
  const branchOptions = useMemo(() => {
    const seen = [];
    (programs[activeProgram] || []).forEach((r) => {
      if (!seen.includes(r.branch_or_stream)) seen.push(r.branch_or_stream);
    });
    return seen;
  }, [programs, activeProgram]);

  const matchesScores = (rec) => {
    const s = rec.snapshot || {};
    if (demand !== 'any' && !(s.future_demand >= Number(demand))) return false;
    if (impact !== 'any' && !(s.ai_agent_impact >= Number(impact))) return false;
    if (risk !== 'any') {
      const r = s.automation_risk;
      if (risk === 'low' && !(r <= 2)) return false;
      if (risk === 'medium' && r !== 3) return false;
      if (risk === 'high' && !(r >= 4)) return false;
    }
    return true;
  };

  const searching = search.trim().length > 0;

  /* Global search across ALL programs by title */
  const globalResults = useMemo(() => {
    if (!searching) return [];
    const q = search.trim().toLowerCase();
    const out = [];
    programNames.forEach((p) => {
      (programs[p] || []).forEach((r) => {
        if (r.title.toLowerCase().includes(q) && matchesScores(r)) out.push(r);
      });
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, programs, demand, impact, risk]);

  /* Tab view: filter + group by branch_or_stream (JSON order) */
  const grouped = useMemo(() => {
    const recs = (programs[activeProgram] || []).filter(
      (r) => (branch === 'any' || r.branch_or_stream === branch) && matchesScores(r)
    );
    const groups = [];
    recs.forEach((r) => {
      let g = groups.find((x) => x.branch === r.branch_or_stream);
      if (!g) { g = { branch: r.branch_or_stream, items: [] }; groups.push(g); }
      g.items.push(r);
    });
    return groups;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programs, activeProgram, branch, demand, impact, risk]);

  const activeFilterCount = [branch, demand, impact, risk].filter((v) => v !== 'any').length;
  const clearFilters = () => { setBranch('any'); setDemand('any'); setImpact('any'); setRisk('any'); };

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="pt-36 px-6 max-w-xl mx-auto text-center">
          <AlertTriangle className="mx-auto text-amber-500 mb-3" size={30} />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Could not load the preview library</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  if (locked) {
    return (
      <div className="min-h-screen bg-[#FFF9F1]">
        <Navbar />
        <div className="pt-32 px-4"><ContentLockedCard title="Preview library isn't open right now" /></div>
        <Footer />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FFF9F1] flex items-center justify-center text-gray-500">
        <Loader2 className="animate-spin mr-2" size={18} /> Loading preview library…
      </div>
    );
  }

  const selectCls = 'h-10 px-3 rounded-xl border-2 border-gray-200 bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40';

  return (
    <div className="min-h-screen bg-[#FFF9F1]">
      <Navbar />

      {/* ── Hero (playful gradient, like After 10th/Inter) ── */}
      <section className="relative pt-32 md:pt-36 pb-16 md:pb-20 px-4 md:px-6 bg-gradient-to-br from-teal-500 via-emerald-500 to-cyan-600 text-white rounded-b-[2.5rem] overflow-hidden">
        <div className="absolute -top-10 -right-10 w-56 h-56 rounded-full bg-white/10" aria-hidden="true" />
        <div className="absolute bottom-0 left-10 w-32 h-32 rounded-full bg-white/10" aria-hidden="true" />
        <div className="max-w-6xl mx-auto relative">
          <span className="inline-flex items-center gap-2 bg-white/20 backdrop-blur px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4">
            <Bot size={13} /> {data.meta?.version || 'Preview Library'}
          </span>
          <h1 className="text-3xl md:text-5xl font-black leading-tight mb-3 cc-pop-in" data-testid="pl-title">
            {data.meta?.title || 'Specialization Previews'}
          </h1>
          <p className="text-white/90 text-base md:text-lg max-w-2xl">
            Explore every specialization with AI-agent workflows, self-check quizzes, real industry scenarios and readiness roadmaps.
          </p>
          {/* validation indicator */}
          {validation && (
            <div className="mt-5 flex flex-wrap items-center gap-2 text-sm" data-testid="pl-validation">
              <span className={`inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-full font-bold backdrop-blur ${validation.ok ? 'bg-white/20 text-white' : 'bg-amber-400/30 text-amber-50'}`}>
                {validation.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                {validation.total} previews loaded
                {' '}({PROGRAM_ORDER.map((p) => validation.counts[p] ?? 0).join(' + ')})
              </span>
              {!validation.ok && (
                <span className="text-amber-100 text-xs" data-testid="pl-validation-warning">
                  Data check warning: {validation.problems.join(' · ')}
                </span>
              )}
            </div>
          )}
        </div>
      </section>

      {/* ── Search + filters card (floating over hero) ── */}
      <section className="px-4 md:px-6 -mt-8 relative z-10">
        <div className="max-w-6xl mx-auto">
          {user?.lexi_stage && (
            <StageFilterBar
              stage={user.lexi_stage}
              showAll={showAll}
              setShowAll={setShowAll}
              contextLabel="specializations"
            />
          )}
        <div className="bg-white border-2 border-gray-100 rounded-3xl shadow-lg shadow-gray-200/60 p-4 md:p-5 cc-pop-in">
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <div className="relative flex-1">
              <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search all 3 programs by specialization title…"
                className="w-full h-11 pl-10 pr-9 rounded-2xl border-2 border-gray-200 bg-gray-50 text-base text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500/40 focus:bg-white transition-colors"
                data-testid="pl-search-input"
              />
              {searching && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600" data-testid="pl-search-clear">
                  <X size={15} />
                </button>
              )}
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`h-11 inline-flex items-center gap-2 px-5 rounded-2xl border-2 text-sm font-bold transition-colors ${showFilters || activeFilterCount > 0 ? 'border-teal-400 bg-teal-50 text-teal-800' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
              data-testid="pl-filter-toggle"
            >
              <SlidersHorizontal size={15} /> Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
            </button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="pl-filters">
              <select value={branch} onChange={(e) => setBranch(e.target.value)} className={selectCls} data-testid="pl-filter-branch" disabled={searching}>
                <option value="any">All branches{searching ? ' (tab only)' : ''}</option>
                {branchOptions.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
              <select value={demand} onChange={(e) => setDemand(e.target.value)} className={selectCls} data-testid="pl-filter-demand">
                {DEMAND_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select value={impact} onChange={(e) => setImpact(e.target.value)} className={selectCls} data-testid="pl-filter-impact">
                {IMPACT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <select value={risk} onChange={(e) => setRisk(e.target.value)} className={selectCls} data-testid="pl-filter-risk">
                {RISK_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              {activeFilterCount > 0 && (
                <button onClick={clearFilters} className="text-xs text-gray-500 hover:text-rose-600 inline-flex items-center gap-1 sm:col-span-2 lg:col-span-4 justify-self-start" data-testid="pl-filter-clear">
                  <X size={12} /> Clear all filters
                </button>
              )}
            </div>
          )}
        </div>
        </div>
      </section>

      {/* ── Program tabs (hidden during global search) ── */}
      {!searching && (
        <section className="px-4 md:px-6 mt-6">
          <div className="max-w-6xl mx-auto flex gap-2 overflow-x-auto no-scrollbar">
            {programNames.map((p) => {
              const Icon = PROGRAM_ICONS[p] || GraduationCap;
              const active = p === activeProgram;
              return (
                <button
                  key={p}
                  onClick={() => { setActiveProgram(p); setBranch('any'); }}
                  className={`shrink-0 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-extrabold border-2 transition-all ${active ? `bg-gradient-to-r ${PROGRAM_GRADS[p]} text-white border-transparent shadow-md` : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
                  data-testid={`pl-tab-${programSlug(p)}`}
                >
                  <Icon size={15} /> {p} ({programs[p]?.length || 0})
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* ── Results ── */}
      <section className="px-4 md:px-6 py-8 pb-16">
        <div className="max-w-6xl mx-auto">
          {searching ? (
            <>
              <p className="text-sm text-gray-500 mb-4 font-semibold" data-testid="pl-search-summary">
                {globalResults.length} match{globalResults.length === 1 ? '' : 'es'} for “{search.trim()}” across all programs
              </p>
              {globalResults.length === 0 ? (
                <div className="text-center py-16 text-gray-500">No specializations match your search.</div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {globalResults.map((r, i) => (
                    <PreviewCard key={`${r.program}-${r.id}`} rec={r} accent={GROUP_ACCENTS[i % GROUP_ACCENTS.length]} showProgram />
                  ))}
                </div>
              )}
            </>
          ) : grouped.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="pl-empty">No specializations match your filters.</div>
          ) : (
            <div className="space-y-10">
              {grouped.map((g, gi) => {
                const accent = GROUP_ACCENTS[gi % GROUP_ACCENTS.length];
                return (
                  <div key={g.branch} data-testid={`pl-group-${g.branch.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase()}`}>
                    <div className="flex items-center gap-2.5 mb-4">
                      <span className={`w-2.5 h-2.5 rounded-full ${accent.dot}`} />
                      <h2 className="text-lg md:text-xl font-extrabold text-gray-900">{g.branch}</h2>
                      <span className="text-xs font-bold text-gray-400">{g.items.length} specialization{g.items.length === 1 ? '' : 's'}</span>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {g.items.map((r) => <PreviewCard key={r.id} rec={r} accent={accent} />)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
